import type { IPOI } from '@/shared/api/types/poi-types'
import type { ITourPoi } from '@/shared/api/types/tour-types'
import { AlertTriangle, ChevronDown, ChevronUp, Search, Star, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CSS_COLOR_NAMES,
  CSS_COLOR_NAME_SET,
  DEFAULT_CSS_COLOR_NAME,
} from '@/shared/constants/css-color-names'
import { cn } from '@/shared/libs/utils'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { resolvePoiCategoryName } from '@/shared/utils/poi-category-utils'

interface TourPoiOrderListProps {
  selectedPois: ITourPoi[]
  poiMap: Record<string, IPOI>
  languageCode?: string
  /** Khi đổi thứ tự (lên/xuống) — chỉ cập nhật local + debounce reorder API ở parent. */
  onReorderMove: (next: ITourPoi[]) => void
  /** Khi xóa đã chọn — parent gọi DELETE rồi cập nhật state (hoặc chỉ local khi tạo tour). */
  onRemoveSelected: (next: ITourPoi[]) => void | Promise<void>
  outOfScopePoiIds?: ReadonlySet<string>
  scopeLabel?: string
  isProcessing?: boolean
}

function normalizeOrder(items: ITourPoi[]): ITourPoi[] {
  return items.map((item, index) => ({ ...item, order: index + 1 }))
}

function getDisplayColorName(color: string | null | undefined): string {
  const normalizedColor = color?.trim().toLowerCase()
  return normalizedColor && CSS_COLOR_NAME_SET.has(normalizedColor)
    ? normalizedColor
    : DEFAULT_CSS_COLOR_NAME
}

interface ColorPickerPopoverProps {
  value: string
  disabled?: boolean
  onSelect: (color: string) => void
}

function ColorPickerPopover({ value, disabled, onSelect }: ColorPickerPopoverProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return q ? CSS_COLOR_NAMES.filter((n) => n.includes(q)) : CSS_COLOR_NAMES
  }, [search])

  const isTransparent = value === 'transparent'

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          title={value}
          aria-label={`Màu POI: ${value}`}
          className={cn(
            'h-7 w-7 rounded border-2 transition-all cursor-pointer shrink-0',
            'hover:scale-110 hover:border-zinc-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400',
            'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100',
            isTransparent
              ? 'border-zinc-300 bg-[repeating-conic-gradient(#e5e5e5_0%_25%,white_0%_50%)] bg-[length:8px_8px]'
              : 'border-zinc-200',
          )}
          style={isTransparent ? undefined : { backgroundColor: value }}
        />
      </PopoverTrigger>
      <PopoverContent className="w-60 p-3" align="end" side="top">
        <p className="mb-2 text-xs font-semibold text-zinc-700">Chọn màu POI</p>
        <div className="relative mb-2">
          <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            className="h-7 pl-6 text-xs"
            placeholder="Tìm tên màu..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-9 gap-1 max-h-44 overflow-y-auto pr-0.5">
          {filtered.map((colorName) => {
            const isSelected = colorName === value
            const isTransp = colorName === 'transparent'
            return (
              <button
                key={colorName}
                type="button"
                title={colorName}
                aria-label={colorName}
                aria-pressed={isSelected}
                className={cn(
                  'h-6 w-6 rounded transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400',
                  isSelected
                    ? 'ring-2 ring-offset-1 ring-zinc-800 border-transparent scale-110'
                    : 'border border-zinc-200',
                  isTransp &&
                    'bg-[repeating-conic-gradient(#e5e5e5_0%_25%,white_0%_50%)] bg-[length:6px_6px]',
                )}
                style={isTransp ? undefined : { backgroundColor: colorName }}
                onClick={() => {
                  onSelect(colorName)
                  setOpen(false)
                  setSearch('')
                }}
              />
            )
          })}
        </div>
        {filtered.length === 0 && (
          <p className="py-3 text-center text-xs text-zinc-400">Không tìm thấy màu</p>
        )}
        <div className="mt-2 flex items-center gap-1.5 border-t border-zinc-100 pt-2">
          <span
            className={cn(
              'h-4 w-4 rounded border border-zinc-200 shrink-0',
              isTransparent &&
                'bg-[repeating-conic-gradient(#e5e5e5_0%_25%,white_0%_50%)] bg-[length:6px_6px]',
            )}
            style={isTransparent ? undefined : { backgroundColor: value }}
          />
          <span className="text-xs text-zinc-500 truncate">{value}</span>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export function TourPoiOrderList({
  selectedPois,
  poiMap,
  languageCode = '',
  onReorderMove,
  onRemoveSelected,
  outOfScopePoiIds,
  scopeLabel,
  isProcessing,
}: TourPoiOrderListProps) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [checkedPoiIds, setCheckedPoiIds] = useState<string[]>([])

  const orderedPois = useMemo(
    () => selectedPois.slice().sort((a, b) => a.order - b.order),
    [selectedPois],
  )

  const filteredPois = useMemo(() => {
    const raw = search.trim()
    if (!raw) return orderedPois

    const q = raw.toLowerCase()
    const orderFromQuery = raw
      .replace(/^#/, '')
      .trim()
      .match(/^(\d+)$/)
    const orderFilter = orderFromQuery ? Number.parseInt(orderFromQuery[1] ?? '', 10) : Number.NaN

    return orderedPois.filter((item, index) => {
      const displayNum = index + 1

      if (Number.isFinite(orderFilter) && displayNum === orderFilter) {
        return true
      }

      const poi = poiMap[item.poiId]
      const nameRaw =
        poi?.localizedData.find((tr) => tr.language === languageCode)?.name ??
        poi?.localizedData[0]?.name ??
        ''
      const haystack = (nameRaw.trim() || item.poiId).toLowerCase()

      return haystack.includes(q)
    })
  }, [languageCode, orderedPois, poiMap, search])

  const checkedCount = checkedPoiIds.filter((id) =>
    orderedPois.some((item) => item.poiId === id),
  ).length
  const outOfScopeCount = useMemo(
    () =>
      orderedPois.reduce((count, item) => count + (outOfScopePoiIds?.has(item.poiId) ? 1 : 0), 0),
    [orderedPois, outOfScopePoiIds],
  )

  function commit(next: ITourPoi[]) {
    onReorderMove(normalizeOrder(next))
  }

  function toggleChecked(poiId: string) {
    setCheckedPoiIds((prev) => {
      if (prev.includes(poiId)) return prev.filter((id) => id !== poiId)
      return [...prev, poiId]
    })
  }

  function moveByPoiId(poiId: string, delta: -1 | 1) {
    const index = orderedPois.findIndex((item) => item.poiId === poiId)
    if (index < 0) return

    const target = index + delta
    if (target < 0 || target >= orderedPois.length) return

    const next = [...orderedPois]
    const current = next[index]
    const swapped = next[target]
    if (!current || !swapped) return

    next[index] = swapped
    next[target] = current
    commit(next)
  }

  function removeSelected() {
    const removeSet = new Set(checkedPoiIds)
    const next = normalizeOrder(orderedPois.filter((item) => !removeSet.has(item.poiId)))
    void onRemoveSelected(next)
    setCheckedPoiIds([])
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-zinc-500">
          {t('tour.selectedPois', { defaultValue: 'POI đã chọn' })}: {orderedPois.length}
        </p>
        <Button
          size="sm"
          variant="outline"
          disabled={checkedCount === 0 || isProcessing}
          onClick={removeSelected}
          className="text-red-600 border-red-200 hover:bg-red-50"
        >
          <Trash2 size={12} className="mr-1" />
          {t('tour.removeSelected', { defaultValue: 'Xóa đã chọn' })} ({checkedCount})
        </Button>
      </div>

      {outOfScopeCount > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-amber-800">
          <p className="flex items-start gap-2 text-xs leading-relaxed">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>
              {t('tour.outOfScopeWarning', {
                defaultValue: '{{count}} POI đã chọn đang nằm ngoài phạm vi hiện tại.',
                count: outOfScopeCount,
              })}
              {scopeLabel ? ` (${scopeLabel})` : ''}
            </span>
          </p>
        </div>
      )}

      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input
          className="pl-8"
          placeholder={t('tour.searchSelectedPoi', { defaultValue: 'Tìm trong POI đã chọn...' })}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="border border-zinc-200 rounded-lg divide-y divide-zinc-100 max-h-[420px] overflow-y-auto">
        {filteredPois.map((item) => {
          const poi = poiMap[item.poiId]
          const poiId = item.poiId
          const checked = checkedPoiIds.includes(poiId)
          const actualIndex = orderedPois.findIndex((entry) => entry.poiId === poiId)
          const name =
            poi?.localizedData.find((tr) => tr.language === languageCode)?.name ??
            poi?.localizedData[0]?.name ??
            poiId
          const areaName = poi?.destinationName?.trim() ?? ''
          const isOutOfScope = outOfScopePoiIds?.has(poiId) ?? false
          const categoryName = resolvePoiCategoryName(
            poi?.categoryData,
            languageCode ? [languageCode] : [],
          )
          const colorValue = getDisplayColorName(item.color)

          return (
            <div
              key={poiId}
              className={cn(
                'flex items-start gap-2 px-3 py-2.5 transition-colors',
                checked ? 'bg-blue-50/60' : 'hover:bg-zinc-50',
                isOutOfScope && 'border-l-2 border-l-amber-400',
              )}
            >
              <strong className="text-sm text-zinc-400 w-5 shrink-0 pt-0.5 tabular-nums">
                {actualIndex + 1}
              </strong>
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-zinc-300 cursor-pointer"
                checked={checked}
                onChange={() => toggleChecked(poiId)}
              />
              <div className="flex-1 min-w-0">
                {/* Row 1: name + area */}
                <p className="text-sm font-medium text-zinc-800 leading-snug">{name}</p>
                <p className="mt-0.5 text-xs text-zinc-400 line-clamp-1">
                  {areaName && areaName.length > 0
                    ? areaName
                    : t('tour.poiNoArea', { defaultValue: 'Chưa gán điểm đến' })}
                </p>

                {/* Row 2: badges + rank + color */}
                <div className="mt-1.5 flex flex-wrap items-center gap-1">
                  {poi?.category && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-zinc-100 text-zinc-500">
                      {categoryName || poi.category}
                    </span>
                  )}
                  {poi?.type && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-600">
                      {poi.type}
                    </span>
                  )}
                  {poi && (
                    <span
                      className={cn(
                        'inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium',
                        poi.isActive
                          ? 'bg-emerald-50 text-emerald-600'
                          : 'bg-zinc-100 text-zinc-400',
                      )}
                    >
                      {poi.isActive ? t('common.active') : t('common.inactive')}
                    </span>
                  )}
                  {isOutOfScope && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-amber-100 text-amber-700">
                      {t('tour.outOfScopeBadge', { defaultValue: 'Ngoài phạm vi' })}
                    </span>
                  )}

                  {/* Rank */}
                  <div
                    className="flex items-center gap-0.5"
                    title={t('tour.favoritePOIRank', { defaultValue: 'Rank POI nổi bật' })}
                  >
                    <Star size={10} className="text-amber-400 fill-amber-400 shrink-0" />
                    <input
                      type="number"
                      className="h-6 w-11 rounded border border-zinc-200 px-1 text-xs text-center tabular-nums focus:outline-none focus:ring-1 focus:ring-zinc-300"
                      value={item.favoritePoiRank ?? ''}
                      min={1}
                      placeholder="—"
                      disabled={isProcessing}
                      onChange={(e) => {
                        const val =
                          e.target.value === '' ? null : Number.parseInt(e.target.value, 10)
                        const updated = selectedPois.map((p) =>
                          p.poiId === poiId ? { ...p, favoritePoiRank: val } : p,
                        )
                        onReorderMove(updated)
                      }}
                    />
                  </div>

                  {/* Color */}
                  <div className="flex items-center gap-1">
                    <ColorPickerPopover
                      value={colorValue}
                      disabled={isProcessing ?? false}
                      onSelect={(nextColor) => {
                        const updated = selectedPois.map((p) =>
                          p.poiId === poiId ? { ...p, color: nextColor } : p,
                        )
                        onReorderMove(updated)
                      }}
                    />
                    <span className="text-xs text-zinc-500 w-20 truncate">{colorValue}</span>
                  </div>
                </div>
              </div>

              {/* Reorder — top-right, aligned with name */}
              <div className="flex items-center gap-0.5 shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={actualIndex <= 0}
                  onClick={() => moveByPoiId(poiId, -1)}
                >
                  <ChevronUp size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={actualIndex === orderedPois.length - 1 || actualIndex < 0}
                  onClick={() => moveByPoiId(poiId, 1)}
                >
                  <ChevronDown size={14} />
                </Button>
              </div>
            </div>
          )
        })}
        {filteredPois.length === 0 && (
          <div className="px-3 py-6 text-center text-sm text-zinc-400">{t('common.noData')}</div>
        )}
      </div>
    </div>
  )
}
